import { useEffect } from 'react';
import { css } from '@emotion/react';
import {
  useAnalyticsOrgLocations,
  useTimePeriodPopoverSelector,
  defaultTimePeriods,
  CheckListPopoverFilterSelector,
} from '@frontend/analytics-shared';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { useAnalyticsReportingShallowStore } from '../hooks/use-analytics-reporting-store';

export const Filters = () => {
  const { t } = useTranslation('analytics-reporting');

  const { filters, onFiltersChange } = useAnalyticsReportingShallowStore('filters', 'onFiltersChange');
  const filteredLocationsCount = filters?.locationIds?.length || 0;

  const { isLoadingLocations, locationNames, preselectedAccessibleLocationIds } = useAnalyticsOrgLocations({
    isAllAccessScopeEnabled: true,
    module: 'PHONE',
  });

  const { defaultStartDate, defaultEndDate, TimePeriodPopoverSelector } = useTimePeriodPopoverSelector({
    defaultPeriod: 'lastMonth',
    timePeriods: defaultTimePeriods,
    values: {
      endDate: filters?.endDate,
      startDate: filters?.startDate,
    },
  });

  useEffect(() => {
    // Do nothing if startDate or endDate is already set from config
    if (filters?.startDate || filters?.endDate) {
      return;
    }

    if (defaultStartDate && defaultEndDate) {
      onFiltersChange({
        startDate: defaultStartDate,
        endDate: defaultEndDate,
      });
    }
  }, [defaultStartDate, defaultEndDate, filters, onFiltersChange]);

  useEffect(() => {
    if (!filters?.locationIds) {
      onFiltersChange({ locationIds: preselectedAccessibleLocationIds });
    }
  }, [filters?.locationIds, onFiltersChange, preselectedAccessibleLocationIds]);

  return (
    <div css={styles.container}>
      <CheckListPopoverFilterSelector<string>
        defaultValue={preselectedAccessibleLocationIds}
        disabled={isLoadingLocations}
        dropdownLikeStyle
        dropdownStyleLabel={t('Locations')}
        label={
          !filteredLocationsCount || filteredLocationsCount > 1
            ? t('{{filteredLocationsCount}} Locations', { filteredLocationsCount })
            : locationNames[filters?.locationIds?.[0] || ''] || t('Select Locations')
        }
        onChange={(locationIds) => {
          onFiltersChange({ locationIds });
        }}
        options={locationNames}
        value={filters?.locationIds}
        trackingId='analytics-reporting-locations-filter'
      />
      <TimePeriodPopoverSelector
        dropdownLikeStyle
        onChange={({ startDate, endDate }) => {
          onFiltersChange({
            startDate,
            endDate,
          });
        }}
        trackingIdBase='analytics-reporting-time-period-filter'
      />
    </div>
  );
};

const styles = {
  container: css`
    display: grid;
    gap: ${theme.spacing(2)};
    grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));
  `,
};
